import { useState } from "react";
import ReferenceForm from "../../../EmissionFactorBreakdown/ReferenceForm";
import ReferenceLink from "../../../EmissionFactorBreakdown/ReferenceLink";
import { referableValues } from "../../model/helpers";

const NEW_REFERENCE = "__new__";

/**
 * Where one factor's values are quoted from: a reference per value it
 * carries (the value, GWPs, NCV, density), picked from the library or added
 * here with its link. "All factors" gives every factor of this KPI that
 * carries the value the same reference.
 *
 * @param {object} props
 * @param {import("../../model/helpers").GridFactor} props.factor
 * @param {import("../../../EmissionFactorBreakdown/referencesApi").GhgReference[]} props.references
 * @param {boolean} props.canApplyToAll the KPI has more than one factor
 * @param {(patch: Partial<import("../../model/helpers").GridFactor>) => void} props.onChange
 * @param {(key: string, referenceId: string) => void} props.onApplyToAll
 * @param {() => void} props.onReferencesChanged
 */
export default function FactorReferenceFields({ factor, references, canApplyToAll, onChange, onApplyToAll, onReferencesChanged }) {
  const [creatingFor, setCreatingFor] = useState(null);
  const byId = new Map(references.map((reference) => [String(reference.id), reference]));

  const pick = (key, value) => {
    if (value === NEW_REFERENCE) {
      setCreatingFor(key);
      return;
    }
    onChange({ [key]: value });
  };

  const created = (reference) => {
    onChange({ [creatingFor]: String(reference.id) });
    setCreatingFor(null);
    onReferencesChanged();
  };

  return (
    <div className="kgb-breakdown kgb-references">
      {referableValues(factor).map(({ key, label }) => (
        <div key={key} className="kgb-reference-line">
          <span className="kgb-reference-label">{label}</span>
          <select aria-label={`${label} reference`} value={factor[key]} onChange={(e) => pick(key, e.target.value)}>
            <option value="">No reference</option>
            {references.map((reference) => (
              <option key={reference.id} value={String(reference.id)}>
                {reference.title}
                {reference.note ? ` — ${reference.note}` : ""}
              </option>
            ))}
            <option value={NEW_REFERENCE}>+ New reference with link…</option>
          </select>
          <ReferenceLink reference={byId.get(factor[key]) || null} label="open" />
          {canApplyToAll && factor[key] ? (
            <button type="button" className="kgb-btn kgb-btn--quiet" onClick={() => onApplyToAll(key, factor[key])}>
              Use for all factors
            </button>
          ) : null}
        </div>
      ))}
      {creatingFor ? <ReferenceForm onSaved={created} onCancel={() => setCreatingFor(null)} /> : null}
    </div>
  );
}
